import { publicApi } from "@api";
import CashbackPrompt from "@components/features/CashbackPrompt";
import BottomNavigation from "@components/navigation/BottomNavigation";
import IframeDialog from "@components/utils/IframeDialog";
import { LazyLoadComponent } from "@components/utils/LazyLoadComponent";
import UserModeRenderer from "@components/utils/UserModeRenderer";
import { envConfig } from "@configs/env";
import {
  NOTIFICATION_DRAWER_ROUTES,
  NotificationDrawerEnabledStatus,
  ONBOARDING_DIALOG_CLASS_NAME,
  Paths,
} from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import { useInGameCashbackPrompt } from "@hooks/useInGameCashbackPrompt";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useNotificationHandler } from "@hooks/useNotificationHandler";
import usePostLoginInfo from "@hooks/usePostLoginInfo";
import usePromotionPageLandedTracker from "@hooks/usePromotionPageLandedTracker";
import useTypedSelector from "@hooks/useTypedSelector";
import { AuthStatus, UserMode, _MemberSettingOnboardDialogLayout } from "@types";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { isDailyRewardModalOpen } from "@utils/ui";
import { PromotionFABGroup } from "@views/HomePage/components";
import {
  MemberSettingAuthMode,
  MemberSettingPlayerNameCollectionMode,
  Page,
  PageContent,
  PageFooter,
  SW_MEMBER_ID_STORAGE_KEY,
  cn,
  updatePushSubscription,
  useHandleApiResponse,
  useResetScroll,
} from "kz-ui-sdk";
import { debounce } from "lodash-es";
import { lazy, useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../PageHeader";

interface PrivateRouteProps {
  mode?: UserMode;
}

const ignoreLayoutInRoutes = [
  Paths.PRIVATE.AUTH_RESET_PAGE,
  Paths.PRIVATE.DEPOSIT_CALLBACK,
  Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE,
  Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE,
  Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE,
];

const ignorePaddingBottomInRoutes: string[] = [Paths.PRIVATE.PROMOTION_DAILY_LOGIN];

const LazyDepositDrawer = lazy(() => import(/* webpackChunkName: "DepositDrawer" */ "@components/utils/DepositDrawer"));

const LazyWithdrawDrawer = lazy(
  () => import(/* webpackChunkName: "WithdrawDrawer" */ "@components/utils/WithdrawDrawer"),
);
const LazyOnboardingDialog = lazy(
  () => import(/* webpackChunkName: "OnboardingDialog" */ "@components/utils/OnboardingDialog"),
);
const LazyNotificationDrawer = lazy(
  () => import(/* webpackChunkName: "NotificationDrawer" */ "@components/utils/NotificationDrawer"),
);

/**
 * Private route layout
 * Display header, content, footer, and bottom navigation
 * Protected routes that require authentication:
 * - Redirect to login page if unauthenticated
 * - Redirect to reset password page if password expired
 * - Redirect to update bank page if no default bank
 */
const PrivateRouteLayout = ({ mode }: PrivateRouteProps) => {
  const refMainBody = useRef<HTMLDivElement>(null);
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { status: authStatus, self, oauth, defaultBankAccount, userMode } = useAuthSession();
  const [createResetPwdMutation] = publicApi.useCreateForgotPasswordMutation();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const { t } = useTranslation();
  const isDisplayAppInstaller = useTypedSelector((state) => state.preferences.isDisplayAppInstaller);
  const { isFulfilled: isPostLoginInfoFulfilled, triggerForm: triggerPostLoginInfoForm } = usePostLoginInfo();
  const { authMode, nameCollectionMode } = useMemberSettings();
  const refPostLoginInfoFormTimeout = useRef<NodeJS.Timeout | null>(null);

  const { openIfEligible, notificationDrawerEnabledStatus, checkPermissionAndSubscribe } = useNotificationHandler();
  const cashbackPrompt = useInGameCashbackPrompt();
  usePromotionPageLandedTracker();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResetSecret = useCallback(
    debounce((phone: string) => {
      if (authMode === MemberSettingAuthMode.PhonePin) {
        toast.error(t("your pin has expired. please reset your pin."));
        createResetPwdMutation({
          phone,
          accountId: envConfig.accountId,
        }).then((res) => {
          handleApiResponse(res, {
            errorMessageResolver: (error) => resolveAuthResponseError(error, t),
          });
        });
      }

      if (authMode === MemberSettingAuthMode.UsernamePassword) {
        toast.error(t("your password has expired. please reset your password."));
      }
    }, 1000),
    [createResetPwdMutation, handleApiResponse, t, authMode],
  );

  useEffect(() => {
    // Redirect to login page if unauthenticated or authenticating
    if (authStatus === AuthStatus.Unauthenticated && mode === UserMode.MEMBER) {
      navigate(Paths.PRIVATE.HOME);
    }
    // Redirect to reset password page if password expired
    if (authStatus === AuthStatus.PasswordExpired && self?.member?.phone) {
      // If user is already in reset password page, show toast message and send OTP
      if (pathname === Paths.PRIVATE.AUTH_RESET_PAGE) {
        handleResetSecret(self.member.phone);
      } else {
        // Redirect to reset password page
        navigate(Paths.PRIVATE.AUTH_RESET_PAGE, {
          replace: true,
        });
      }
      return;
    }
  }, [authStatus, defaultBankAccount, handleResetSecret, navigate, pathname, self?.member?.phone, mode]);

  // Update push notification subscription
  useEffect(() => {
    // If notification drawer is not disabled, skip the old push notification subscription logic
    if (
      notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.ENABLED ||
      notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.LOADING
    )
      return;

    if (authStatus === AuthStatus.Authenticated) {
      if (!!self && !!oauth && !!self.member?.id) {
        const registeredMemberId = sessionStorage.getItem(SW_MEMBER_ID_STORAGE_KEY);
        // if member id is not registered or different, update notification subscription
        if (!registeredMemberId || registeredMemberId !== self.member.id) {
          updatePushSubscription({
            memberId: self.member.id,
            accountId: envConfig.accountId,
            notificationURI: import.meta.env.VITE_PUSH_NOTIFICATION_URI,
            notificationTestParam: import.meta.env.VITE_TEST_NOTIFICATION_PARAM,
            publicUrl: import.meta.env.VITE_PUBLIC_URL,
          }).then(() => {});
        }
      }
    }
  }, [authStatus, oauth, self, notificationDrawerEnabledStatus]);

  useEffect(() => {
    // If notification drawer is enabled, open the notification drawer if the current route is in the notification drawer routes
    if (
      notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.ENABLED &&
      NOTIFICATION_DRAWER_ROUTES.some((route) => matchPath(route, pathname))
    ) {
      openIfEligible();
    }
  }, [openIfEligible, pathname, notificationDrawerEnabledStatus]);

  useEffect(() => {
    // Check permission and subscribe when user is authenticated and notification drawer is enabled
    if (notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.ENABLED && !!self?.member?.id) {
      checkPermissionAndSubscribe();
    }
  }, [checkPermissionAndSubscribe, notificationDrawerEnabledStatus, self?.member?.id]);

  // Display layout for all routes except ignored routes
  const displayLayout = useMemo(() => {
    return !ignoreLayoutInRoutes.some((route) => matchPath(route, pathname));
  }, [pathname]);

  // Display footer for all routes except ignored routes
  const displayPaddingBottom = useMemo(() => {
    return !ignorePaddingBottomInRoutes.some((route) => matchPath(route, pathname));
  }, [pathname]);

  // Reset scroll position on route change
  useResetScroll(refMainBody, pathname);

  const displayPostLoginInfoDialog = useCallback(() => {
    if (isDailyRewardModalOpen()) {
      return false;
    }
    // check if current dialog is open, skip open request
    const dialog = document.querySelector(`.${ONBOARDING_DIALOG_CLASS_NAME}`);
    if (dialog) return false;
    triggerPostLoginInfoForm("dialog", undefined, {
      layout: _MemberSettingOnboardDialogLayout.DIALOG_DRAWER,
    });
    return true;
  }, [triggerPostLoginInfoForm]);

  // Force update post login info on route change
  useEffect(() => {
    const ignoreRoutes = [Paths.PRIVATE.BANK_ACCOUNTS, Paths.PRIVATE.E_WALLETS];
    if (
      !isPostLoginInfoFulfilled &&
      authStatus === AuthStatus.Authenticated &&
      !ignoreRoutes.some((route) => matchPath(route, pathname)) &&
      nameCollectionMode === MemberSettingPlayerNameCollectionMode.Registration
    ) {
      refPostLoginInfoFormTimeout.current = setTimeout(() => {
        if (refPostLoginInfoFormTimeout.current) {
          clearTimeout(refPostLoginInfoFormTimeout.current);
        }
        const isDisplayed = displayPostLoginInfoDialog();
        // try again after 400ms if dialog is not display
        if (!isDisplayed) {
          refPostLoginInfoFormTimeout.current = setTimeout(() => {
            displayPostLoginInfoDialog();
          }, 400);
        }
      }, 100);
    }

    return () => {
      if (refPostLoginInfoFormTimeout.current) {
        clearTimeout(refPostLoginInfoFormTimeout.current);
      }
    };
  }, [isPostLoginInfoFulfilled, displayPostLoginInfoDialog, authStatus, pathname, nameCollectionMode]);

  // Show loading spinner/blank if authenticating
  if (authStatus === AuthStatus.Authenticating) return null;

  return (
    <Page className="mx-auto">
      {displayLayout && (
        <PageHeader
          className={cn({
            "top-[40px]": isDisplayAppInstaller,
          })}
        />
      )}

      <PageContent
        ref={refMainBody}
        className={cn("scrollbar-primary page-content mt-[50px] px-5 pt-2.5", {
          "!mt-0 !p-0": !displayLayout,
          "!mt-[90px]": isDisplayAppInstaller && displayLayout,
          "!mt-[40px]": isDisplayAppInstaller && !displayLayout,
          "pb-28": displayPaddingBottom,
          "pb-[56px]": !displayPaddingBottom,
        })}
      >
        <Outlet />
      </PageContent>

      <UserModeRenderer mode={UserMode.MEMBER}>
        <LazyLoadComponent>
          <LazyDepositDrawer />
          <LazyWithdrawDrawer />
          <LazyNotificationDrawer />
        </LazyLoadComponent>
      </UserModeRenderer>
      <PromotionFABGroup />

      <IframeDialog />

      <UserModeRenderer mode={UserMode.MEMBER}>
        <CashbackPrompt
          visible={cashbackPrompt.visible}
          totalClaimable={cashbackPrompt.totalClaimable}
          onDismiss={cashbackPrompt.onDismiss}
        />
      </UserModeRenderer>

      {displayLayout && (
        <PageFooter
          fixed
          className="z-[8]"
        >
          <BottomNavigation
            className="mx-auto max-w-md"
            userMode={userMode}
          />
        </PageFooter>
      )}
    </Page>
  );
};

export default PrivateRouteLayout;
